import * as React from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { TONE_NOTICE_CLASSES, TONE_TEXT_CLASSES, type Tone } from "@/lib/theme/tone";

/**
 * The one primitive for an action *result* (Action-Feedback-System §A1,
 * 2026-08-03). Three properties are the whole reason it exists:
 *
 *  1. **The role is derived, never passed.** `danger`/`warning` announce
 *     assertively (`role="alert"`), everything else politely (`role="status"`).
 *     A warning in this product blocks the action it belongs to (read-only
 *     wind-down, quota, partial success), so it is not a "when you get a
 *     moment" message. Before this, 127 `role="alert"` and 58 `role="status"`
 *     sites picked their own semantics — and `StorageBackendCard.tsx:252`
 *     announced *errors* under `role="status"`.
 *  2. **The live region stays mounted.** A region that enters the DOM together
 *     with its text is announced unreliably by NVDA/JAWS/VoiceOver — they
 *     monitor regions that already existed when the mutation happened. Every
 *     `{saved && <p …>}` in the tree (starting with `BearbeitungForm.tsx:354`)
 *     has exactly that bug. Here the wrapper renders unconditionally and only
 *     the text swaps; `empty:hidden` (plus the explicit `hidden` below, because
 *     `:empty` is defeated by a stray empty text node) keeps it out of the
 *     layout while it has nothing to say.
 *  3. **Colours come only from `lib/theme/tone.ts`.** That ends the divergence
 *     where the same "saved" state rendered as `text-muted-foreground` in
 *     `SyncPanel.tsx:239` and as `text-success` in `StorageBackendCard.tsx:253`.
 *     No raw palette class may appear here or at a call site.
 *
 * For *field* validation use `FieldError`/`FieldHint` instead — those carry no
 * role at all, because a required-field hint re-renders on every keystroke and
 * an assertive region would re-announce it every time (§2.7).
 */

export type StatusMessageVariant = "inline" | "notice";

export interface StatusMessageProps {
  /** Semantic tone — decides colour AND live-region politeness. */
  tone: Tone;
  /** The message. `null`/`""` keeps the region mounted but silent. */
  children: React.ReactNode;
  /** `inline` = bare coloured text (default); `notice` = bordered box. */
  variant?: StatusMessageVariant;
  /** Leading tone icon. Defaults to `true` for `notice`, `false` for `inline`. */
  icon?: boolean;
  /** Passthrough — existing suites query stable testids (e.g. `sync-feedback`). */
  "data-testid"?: string;
  className?: string;
}

/** Tones that interrupt. Both block the action they belong to. */
const ROLE_BY_TONE: Record<Tone, "alert" | "status"> = {
  danger: "alert",
  warning: "alert",
  success: "status",
  info: "status",
  neutral: "status",
};

const ICON_BY_TONE: Record<Tone, LucideIcon> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
  info: Info,
  neutral: Info,
};

/**
 * Does this `children` value produce anything a user can read?
 *
 * `React.Children.toArray` already drops `null`, `undefined` and booleans; the
 * remaining trap is `""`, which React commits as an (invisible) text node and
 * which would therefore defeat `:empty`.
 */
export function hasContent(children: React.ReactNode): boolean {
  return React.Children.toArray(children).some(
    (child) => typeof child !== "string" || child.trim().length > 0,
  );
}

export function StatusMessage({
  tone,
  children,
  variant = "inline",
  icon,
  className,
  "data-testid": testId,
}: StatusMessageProps) {
  const filled = hasContent(children);
  const showIcon = (icon ?? variant === "notice") && filled;
  const ToneIcon = ICON_BY_TONE[tone];

  return (
    <div
      data-slot="status-message"
      data-tone={tone}
      data-variant={variant}
      data-empty={filled ? undefined : "true"}
      data-testid={testId}
      role={ROLE_BY_TONE[tone]}
      className={cn(
        // `inline-flex` for the bare-text variant so it keeps behaving like the
        // `<span>`/`<p>` it replaces inside a flex row or a label line.
        variant === "notice" ? "flex" : "inline-flex",
        "items-start gap-1.5 empty:hidden",
        variant === "notice" ? TONE_NOTICE_CLASSES[tone] : TONE_TEXT_CLASSES[tone],
        // `:empty` alone is not enough — see `hasContent`. Belt and braces, and
        // it is what keeps the node in the DOM instead of unmounting it.
        !filled && "hidden",
        className,
      )}
    >
      {showIcon && <ToneIcon className="mt-px size-4 shrink-0" aria-hidden />}
      {filled && <span className="min-w-0">{children}</span>}
    </div>
  );
}
